/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useId, useReducedMotion } from '@coursera/cds-core';

// ---------------------------------------------------------------------------
// Animation helpers
// ---------------------------------------------------------------------------

type GradientKeyframe = { t: number; tx: number; ty: number };

// Gradient center sweeps: bottom tip → left tip → original top-left.
// 60% of duration for the main sweep, 40% for the final ease into resting state.
const LARGE_GRAD_KEYFRAMES: GradientKeyframe[] = [
  { t: 0, tx: 9.219, ty: 17.011 }, // bottom tip
  { t: 0.6, tx: 2.99, ty: 10.782 }, // left tip
  { t: 1, tx: 2.379, ty: 3.941 }, // original top-left (matches AIGenerateBrandedNewIcon)
];

const SMALL_GRAD_KEYFRAMES: GradientKeyframe[] = [
  { t: 0, tx: 14.666, ty: 7.44 }, // bottom tip
  { t: 0.6, tx: 12.559, ty: 5.585 }, // left tip
  { t: 1, tx: 12.283, ty: 2.95 }, // original
];

// rotate(45°) scale(sx sy) matrix coefficients — only tx/ty change during animation
const LARGE_GRAD_MATRIX = { a: 13.376, b: 13.376, c: -12.17, d: 12.17 };
const SMALL_GRAD_MATRIX = { a: 4.662, b: 4.662, c: -4.24, d: 4.24 };

const SMALL_SPARKLE_DELAY_MS = 1000;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

const buildGradientTransform = ({ a, b, c, d }: typeof LARGE_GRAD_MATRIX, tx: number, ty: number) =>
  `matrix(${a} ${b} ${c} ${d} ${tx.toFixed(3)} ${ty.toFixed(3)})`;

const interpolateGradient = (kfs: GradientKeyframe[], progress: number) => {
  for (let i = 0; i < kfs.length - 1; i += 1) {
    const from = kfs[i] as GradientKeyframe;
    const to = kfs[i + 1] as GradientKeyframe;
    if (progress <= to.t) {
      const t = easeInOut((progress - from.t) / (to.t - from.t));
      return { tx: from.tx + (to.tx - from.tx) * t, ty: from.ty + (to.ty - from.ty) * t };
    }
  }
  const last = kfs[kfs.length - 1] as GradientKeyframe;
  return { tx: last.tx, ty: last.ty };
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const sparkleExpand = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  70%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const styles = {
  svg: css`
    animation: ${fadeIn} 0.4s ease-out forwards;

    .large-sparkle {
      transform-box: fill-box;
      transform-origin: center;
      animation: ${sparkleExpand} 0.6s ease-out both;
    }

    .small-sparkle {
      transform-box: fill-box;
      transform-origin: center;
      animation: ${sparkleExpand} 0.6s ease-out 1s both;
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;

      .large-sparkle {
        animation: none;
      }

      .small-sparkle {
        animation: none;
        opacity: 1;
      }
    }
  `,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SIZE_PX: Record<string, number> = {
  small: 16,
  medium: 20,
  large: 24,
  xlarge: 32,
};

type Props = {
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** Animation duration in milliseconds. @default 3000 */
  durationMs?: number;
};

const AnimatedAIGenerateBrandedIcon = ({ size = 'medium', durationMs = 3000 }: Props) => {
  const baseId = useId();
  const grad0Id = `${baseId}-sparkle-grad-0`;
  const grad1Id = `${baseId}-sparkle-grad-1`;

  const px = SIZE_PX[size] ?? 20;
  const prefersReducedMotion = useReducedMotion();

  const [largeTransform, setLargeTransform] = useState(() =>
    buildGradientTransform(LARGE_GRAD_MATRIX, LARGE_GRAD_KEYFRAMES[0]?.tx ?? 0, LARGE_GRAD_KEYFRAMES[0]?.ty ?? 0)
  );
  const [smallTransform, setSmallTransform] = useState(() =>
    buildGradientTransform(SMALL_GRAD_MATRIX, SMALL_GRAD_KEYFRAMES[0]?.tx ?? 0, SMALL_GRAD_KEYFRAMES[0]?.ty ?? 0)
  );

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const mountTime = performance.now();
    let rafId: number;

    const tick = (now: number) => {
      const elapsed = now - mountTime;

      const largeProgress = Math.min(elapsed / durationMs, 1);
      const { tx: ltx, ty: lty } = interpolateGradient(LARGE_GRAD_KEYFRAMES, largeProgress);
      setLargeTransform(buildGradientTransform(LARGE_GRAD_MATRIX, ltx, lty));

      if (elapsed >= SMALL_SPARKLE_DELAY_MS) {
        const smallProgress = Math.min((elapsed - SMALL_SPARKLE_DELAY_MS) / durationMs, 1);
        const { tx: stx, ty: sty } = interpolateGradient(SMALL_GRAD_KEYFRAMES, smallProgress);
        setSmallTransform(buildGradientTransform(SMALL_GRAD_MATRIX, stx, sty));
      }

      if (elapsed < durationMs + SMALL_SPARKLE_DELAY_MS) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [durationMs, prefersReducedMotion]);

  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="animated-ai-generate-branded-icon"
      css={styles.svg}
      aria-hidden="true"
    >
      <defs>
        <radialGradient
          id={grad0Id}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform={largeTransform}
        >
          <stop stopColor="#6923DE" />
          <stop offset="1" stopColor="#3286FF" />
        </radialGradient>
        <radialGradient
          id={grad1Id}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform={smallTransform}
        >
          <stop stopColor="#6923DE" />
          <stop offset="1" stopColor="#3286FF" />
        </radialGradient>
      </defs>
      {/* Large sparkle */}
      <g className="large-sparkle">
        <path
          d="M8.766 4.552C8.932 4.146 9.506 4.146 9.672 4.552L10.568 6.751C11.065 7.968 12.032 8.935 13.250 9.430L15.448 10.328C15.853 10.494 15.853 11.069 15.448 11.236L13.250 12.131C12.032 12.628 11.065 13.595 10.568 14.813L9.672 17.011C9.506 17.417 8.932 17.417 8.766 17.011L7.875 14.813C7.377 13.595 6.403 12.628 5.190 12.131L2.990 11.236C2.583 11.069 2.583 10.494 2.990 10.328L5.190 9.430C6.403 8.935 7.377 7.968 7.875 6.751L8.766 4.552Z"
          fill={`url(#${grad0Id})`}
        />
      </g>
      {/* Small sparkle (upper-right) — expands in after the large sparkle */}
      <g className="small-sparkle">
        <path
          d="M14.414 3.226C14.506 3.000 14.826 3.000 14.918 3.226L15.312 4.189C15.404 4.416 15.583 4.596 15.809 4.688L16.773 5.081C16.999 5.173 16.999 5.493 16.773 5.585L15.809 5.979C15.583 6.072 15.404 6.250 15.312 6.478L14.918 7.440C14.826 7.666 14.506 7.666 14.414 7.440L14.020 6.478C13.928 6.250 13.749 6.072 13.523 5.979L12.559 5.585C12.333 5.493 12.333 5.173 12.559 5.081L13.523 4.688C13.749 4.596 13.928 4.416 14.020 4.189L14.414 3.226Z"
          fill={`url(#${grad1Id})`}
        />
      </g>
    </svg>
  );
};

export default AnimatedAIGenerateBrandedIcon;
